// components/Accounting/ImportModal.js
import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { withTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import {
  Modal,
  ModalHeader,
  ModalBody,
  Row,
  Col,
  Label,
  Input,
  Button
} from 'reactstrap'
import { importClassAccount as onImportClassAccount } from '../../../slice/thunks'

const ImportModal = ({ isOpen, toggle, onImportSuccess, t }) => {
  const dispatch = useDispatch()
  const [importFile, setImportFile] = useState(null)
  const [isImporting, setIsImporting] = useState(false)
  const [dragOver, setDragOver] = useState(false)
  
  const { ClassAccountImportSuccess, error } = useSelector(state => ({
    ClassAccountImportSuccess: state.Settings.ClassAccountImportSuccess,
    error: state.Settings.error
  }))

  useEffect(() => {
    if (ClassAccountImportSuccess && !error && isImporting) {
      setImportFile(null)
      setIsImporting(false)
      // Appeler onImportSuccess AVANT de fermer le modal
      onImportSuccess()
      // Petit délai pour laisser le temps au refresh de se faire
      setTimeout(() => {
        toggle()
       // toast.success(t("import_success"))
      }, 100)
    } else if (error && isImporting) {
      setIsImporting(false)
      toast.error(error.message || t("import_error"))
    }
  }, [ClassAccountImportSuccess, error, isImporting])

  const handleFileChange = (event) => {
    const file = event.target.files[0]
    validateAndSetFile(file)
  }

  const validateAndSetFile = (file) => {
    if (!file) return

    const allowedTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel'
    ]
    
    if (!allowedTypes.includes(file.type)) {
      toast.error(t("please_select_excel_file"), { autoClose: 5000 })
      return
    }

    const maxSize = 10 * 1024 * 1024 // 10MB
    if (file.size > maxSize) {
      toast.error(t("file_too_large"), { autoClose: 5000 })
      return
    }

    setImportFile(file)
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    setDragOver(true)
  }

  const handleDragLeave = (e) => {
    e.preventDefault()
    setDragOver(false)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setDragOver(false)
    const file = e.dataTransfer.files[0]
    validateAndSetFile(file)
  }

  const handleImport = () => {
    if (!importFile) {
      toast.error(t("please_select_file"), { autoClose: 5000 })
      return
    }
    
    setIsImporting(true)
    dispatch(onImportClassAccount(importFile))
  }

  const handleCancel = () => {
    setImportFile(null)
    setIsImporting(false)
    toggle()
  }

  const removeFile = () => {
    setImportFile(null)
  }

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="md" centered backdrop="static">
      <ModalHeader toggle={!isImporting ? toggle : undefined}>
        <div className="d-flex align-items-center">
          <i className="fas fa-upload text-primary me-2"></i>
          {t("import_class_accounts")}
        </div>
      </ModalHeader>
      
      <ModalBody>
        <Row>
          <Col xs={12}>
            {!importFile ? (
              <div
                className={`border-2 border-dashed rounded p-4 text-center ${
                  dragOver ? 'border-primary bg-light' : 'border-secondary'
                }`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                style={{ minHeight: '200px', cursor: 'pointer' }}
                onClick={() => document.getElementById('file-input').click()}
              >
                <div className="mb-3">
                  <i className="fas fa-cloud-upload-alt fa-3x text-muted"></i>
                </div>
                <h5 className="text-muted mb-2">
                  {t("drag_drop_file")}
                </h5>
                <p className="text-muted mb-3">
                  {t("or_click_to_select")}
                </p>
                <div className="text-muted small">
                  <p className="mb-1">
                    <strong>{t("accepted_formats")}</strong> .xlsx, .xls
                  </p>
                </div>
                <input
                  type="file"
                  id="file-input"
                  className="d-none"
                  accept=".xlsx,.xls"
                  onChange={handleFileChange}
                  disabled={isImporting}
                />
              </div>
            ) : (
              <div className="border rounded p-3">
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <div className="d-flex align-items-center">
                    <i className="fas fa-file-excel text-success me-2 fa-2x"></i>
                    <div>
                      <h6 className="mb-1">{importFile.name}</h6>
                      <small className="text-muted">{formatFileSize(importFile.size)}</small>
                    </div>
                  </div>
                  {!isImporting && (
                    <Button
                      color="danger"
                      size="sm"
                      outline
                      onClick={removeFile}
                    >
                      <i className="fas fa-times"></i>
                    </Button>
                  )}
                </div>
                
                {isImporting && (
                  <div className="mb-3">
                    <div className="d-flex align-items-center">
                      <div className="spinner-border spinner-border-sm text-primary me-2" role="status"></div>
                      <span className="text-primary">{t("importing_please_wait")}</span>
                    </div>
                    <div className="progress mt-2" style={{ height: '6px' }}>
                      <div className="progress-bar progress-bar-striped progress-bar-animated" style={{ width: '100%' }}></div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </Col>
        </Row>

        {importFile && (
          <Row className="mt-3">
     
          </Row>
        )}
      </ModalBody>

      <div className="modal-footer">
        <Button
          color="secondary"
          onClick={handleCancel}
          disabled={isImporting}
        >
          {t("cancel")}
        </Button>
        {importFile && (
          <Button
            color="primary"
            onClick={handleImport}
            disabled={isImporting}
          >
            {isImporting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                {t("importing")}
              </>
            ) : (
              <>
                <i className="fas fa-upload me-2"></i>
                {t("start_import")}
              </>
            )}
          </Button>
        )}
      </div>
    </Modal>
  )
}

export default withTranslation()(ImportModal)